import React, { useState, useEffect } from "react";
import { message, Modal } from "antd";
import { CloseOutlined, QuestionCircleFilled } from "@ant-design/icons";
import { ConfirmFillIcon, RoundIcon } from "@t8891/icon";
import { Input } from "@t8891/mobile";
import { getComfort, postComfort } from "@Services/kindsum";
import { ISpaceData } from "@Types/kindSumReport";
import { useQuery } from "react-query";

interface IProps {
  id: number;
  spaceModelYearId: number;
  spaceKindTitle: string;
}

export interface IScoreData {
  all: {
    score: number;
    tag: string;
  };
  list: IScore[];
}

interface IScore {
  title: string;
  value: string;
}

const Space: React.FC<IProps> = ({ id, spaceKindTitle, spaceModelYearId }) => {
  const [gender, setGender] = useState(1);
  const [height, setHeight] = useState(0);
  const [visible, setVisible] = useState<boolean>(false);

  const [score, setScore] = useState<Partial<IScoreData>>({});

  // 空间实测
  const { data } = useQuery<ISpaceData[]>(["getComfort", spaceModelYearId], async () => {
    if (!spaceModelYearId) return [];
    try {
      const res: { data; status } = await getComfort({ myid: spaceModelYearId });
      if (res?.status === 12000) {
        return [
          { value: res.data.firstHeight || "", title: "前排高度", top: 4, left: 84 },
          { value: res.data.firstLegSpace || "", title: "前排腿部空間", top: 108, left: 0 },
          { value: res.data.firstLength || "", title: "前排內部寬度", top: 188, left: 60 },
          { value: res.data.secondChairHeight || "", title: "後排坐墊高度", top: 188, left: 130 },
          { value: res.data.secondHeight || "", title: "後排高度", top: 4, left: 206 },
          { value: res.data.secondLegSpace || "", title: "後排腿部空間", top: 70, left: 140 },
          { value: res.data.secondLength || "", title: "後排內部寬度", top: 188, left: 194 },
        ];
      }
    } catch (error) {}
    return [];
  });

  const submit = async () => {
    if (!height || typeof height === "string" || height <= 0) {
      message.warning("請填寫正確的身高");
      return;
    }

    const res: { data; status } = await postComfort({ myid: id, gender, height });

    if (res?.status === 12000) {
      setScore({
        all: res.data.all,
        list: [
          {
            title: "前排大腿剩餘長度",
            value: `${res.data.firstLegLength?.tag}${res.data.firstLegLength?.score || 0}分`,
          },
          {
            title: "前排膝蓋剩餘長度",
            value: `${res.data.firstKneeLength?.tag}${res.data.firstKneeLength?.score || 0}分`,
          },
          { title: "前排頭頂剩餘高度", value: `${res.data.chairAngle?.tag}${res.data.chairAngle?.score || 0}分` },
          { title: "後排椅背角度", value: `${res.data.firstHeadHeight?.tag}${res.data.firstHeadHeight?.score || 0}分` },
          {
            title: "後排大腿剩餘長度",
            value: `${res.data.secondLegLength?.tag}${res.data.secondLegLength?.score || 0}分`,
          },
          {
            title: "後排膝蓋剩餘長度",
            value: `${res.data.secondKneeLength?.tag}${res.data.secondKneeLength?.score || 0}分`,
          },
          {
            title: "後排頭頂剩餘高度",
            value: `${res.data.secondHeadHeight?.tag}${res.data.secondHeadHeight?.score || 0}分`,
          },
        ],
      });
      return;
    }
    message.info(res.data.message);
  };

  useEffect(() => {}, []);

  return (
    <div className='softTest w-full px-12'>
      <div className='text-10 text-[#999999] leading-14 mt-8 h-14'>{spaceKindTitle}</div>
      <div className='relative h-224 flex justify-center items-center'>
        <img src='https://photo.8891.com.tw/2024/06/17/1718591741725627.png' alt='picture' className='w-full h-142' />
        {Array.isArray(data) &&
          data.length > 0 &&
          data.map((item, index) => (
            <div
              key={index}
              style={{
                top: item.top,
                left: item.left,
              }}
              className={`flex items-center justify-center flex-col absolute`}
            >
              <span className='text-[#F87F15] text-12 font-medium h-16'>{item.value}</span>
              <p className='text-[#333333] text-10'>{item.title}</p>
            </div>
          ))}
      </div>
      <div className='w-full flex items-center justify-end h-18'>
        {score?.all && (
          <p className='text-[#3264FF] text-12 font-medium'>
            舒適度：{score?.all?.tag}
            {score?.all?.score}分
            <QuestionCircleFilled
              id='ScoreAllTip'
              style={{ color: "#BBBBBB", marginLeft: "8px" }}
              onClick={() => setVisible(true)}
            />
          </p>
        )}
        <p className='text-[#999999] text-10 text-right flex-1 leading-15'>單位：cm</p>
      </div>
      <div className='flex flex-col mt-4'>
        <div className='flex justify-between items-center'>
          <div className='border-1 border-solid border-[#E8E8E8] h-32 w-124 rounded-4 flex justify-center items-center text-12'>
            <Input
              id='HeightInput'
              style={{ "--input-font-size": "12px", width: "80px" }}
              className='w-80'
              placeholder='輸入身高'
              type={"number"}
              onlyShowClearWhenFocus={true}
              onChange={(e: any) => {
                setHeight(Number(e));
              }}
            />
            <span className='text-[#333] text-12'>CM</span>
          </div>
          <div
            className='border-1 border-solid border-[#E8E8E8] h-32 w-124 rounded-4 flex items-center justify-between px-18'
            id='SexSelect'
          >
            <div className='flex justify-center items-center cursor-pointer' onClick={() => setGender(1)}>
              {!!gender ? <ConfirmFillIcon size={32} fill={"#3264FF"} /> : <RoundIcon size={32} />}
              <span className='text-[#333] text-12 ml-4'>男</span>
            </div>
            <div className='flex justify-center items-center cursor-pointer' onClick={() => setGender(0)}>
              {!gender ? <ConfirmFillIcon size={32} fill={"#3264FF"} /> : <RoundIcon size={32} />}
              <span className='text-[#333] text-12 ml-4'>女</span>
            </div>
          </div>
        </div>
        <button
          data-type={`${height}、${!gender ? "女" : "男"}`}
          id='SubmitRideButton'
          className='submit bg-[#3264FF] text-white rounded-4 text-14 h-32 mt-10 cursor-pointer font-normal'
          onClick={() => submit()}
        >
          檢測我的乘坐體驗
        </button>
      </div>

      <Modal
        visible={visible}
        centered
        closeIcon={
          <div className='h-30 flex items-center justify-center ml-26 mt-0'>
            <CloseOutlined />
          </div>
        }
        footer={null}
        onCancel={() => setVisible(false)}
      >
        <div className='flex flex-col items-center justify-center'>
          <h3 className='text-[#333333] text-18 mb-30'>舒適性評分詳情</h3>
          {Array.isArray(score.list) &&
            score.list?.length > 0 &&
            score.list.map((item, index) => (
              <div className='flex items-center mb-8 w-240 justify-start' key={index}>
                <p className='text-[#333333] text-14 w-180'>{item.title}</p>
                <span className='text-[#3264FF] text-14 w-100'>{item.value}</span>
              </div>
            ))}
        </div>
      </Modal>
    </div>
  );
};

export default Space;
